// OrderDetail.js
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatPrice, formatDate } from '../utils/api';
import './Orders.css';

const STATUS_STEPS = ['pending', 'processing', 'shipped', 'delivered'];
const STATUS_ICONS = { pending: '🕐', processing: '⚙️', shipped: '🚚', delivered: '✅', cancelled: '❌', refunded: '↩️' };

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`)
      .then(r => setOrder(r.data.order))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-center" style={{ minHeight: '60vh' }}><div className="spinner"></div></div>;
  if (!order) return <div className="container" style={{ padding: '60px 0', textAlign: 'center' }}>Order not found.</div>;

  const currentStep = STATUS_STEPS.indexOf(order.orderStatus);

  return (
    <div className="orders-page container">
      <div className="order-detail-header">
        <div>
          <h1 className="section-title">Order Details</h1>
          <p className="order-id-text">Order #{order._id.slice(-8).toUpperCase()}</p>
          <p className="order-date-text">Placed on {formatDate(order.createdAt)}</p>
        </div>
        <span className={`status-badge status-${order.orderStatus}`}>
          {STATUS_ICONS[order.orderStatus]} {order.orderStatus}
        </span>
      </div>

      {/* Progress tracker */}
      {!['cancelled', 'refunded'].includes(order.orderStatus) && (
        <div className="progress-tracker card">
          {STATUS_STEPS.map((step, idx) => (
            <div key={step} className={`progress-step ${idx <= currentStep ? 'done' : ''} ${idx === currentStep ? 'current' : ''}`}>
              <div className="progress-dot"></div>
              <span>{step}</span>
              {idx < STATUS_STEPS.length - 1 && <div className={`progress-line ${idx < currentStep ? 'done' : ''}`}></div>}
            </div>
          ))}
        </div>
      )}

      <div className="order-detail-grid">
        {/* Items */}
        <div>
          <div className="card order-items-card">
            <h3 className="card-section-title">Items Ordered</h3>
            {order.orderItems.map(item => (
              <div key={item._id} className="order-item-row">
                <img src={item.image} alt={item.name} className="order-item-img" />
                <div className="order-item-info">
                  <Link to={`/product/${item.product?.slug || item.product?._id}`} className="order-item-name">{item.name}</Link>
                  <p className="order-item-qty">Qty: {item.quantity} × {formatPrice(item.price)}</p>
                </div>
                <span className="order-item-total">{formatPrice(item.price * item.quantity)}</span>
              </div>
            ))}
          </div>

          {/* Shipping info */}
          <div className="card order-meta-card">
            <h3 className="card-section-title">Shipping Address</h3>
            <p>{order.shippingAddress.name}</p>
            <p>{order.shippingAddress.street}</p>
            <p>{order.shippingAddress.city}, {order.shippingAddress.state} – {order.shippingAddress.postalCode}</p>
            <p>{order.shippingAddress.country}</p>
            <p>📞 {order.shippingAddress.phone}</p>
            {order.trackingNumber && (
              <p className="tracking-number">📦 Tracking: <strong>{order.trackingNumber}</strong></p>
            )}
          </div>
        </div>

        {/* Summary */}
        <div>
          <div className="card order-summary-card">
            <h3 className="card-section-title">Payment Summary</h3>
            <div className="summary-row"><span>Subtotal</span><span>{formatPrice(order.itemsPrice)}</span></div>
            <div className="summary-row"><span>Shipping</span><span>{order.shippingPrice === 0 ? 'Free' : formatPrice(order.shippingPrice)}</span></div>
            <div className="summary-row"><span>Tax</span><span>{formatPrice(order.taxPrice)}</span></div>
            <hr className="divider" />
            <div className="summary-row summary-total"><strong>Total</strong><strong>{formatPrice(order.totalPrice)}</strong></div>
            <div className="summary-row">
              <span>Payment Method</span>
              <span className="capitalize">{order.paymentMethod}</span>
            </div>
            <div className="summary-row">
              <span>Payment Status</span>
              <span className={order.isPaid ? 'text-green' : 'text-red'}>
                {order.isPaid ? `✓ Paid on ${formatDate(order.paidAt)}` : '⏳ Pending'}
              </span>
            </div>
          </div>

          {/* Status history */}
          {order.statusHistory?.length > 0 && (
            <div className="card order-meta-card">
              <h3 className="card-section-title">Status History</h3>
              <div className="status-history">
                {order.statusHistory.map((h, i) => (
                  <div key={i} className="history-item">
                    <span className="history-dot"></span>
                    <div>
                      <strong className="capitalize">{h.status}</strong>
                      {h.note && <p>{h.note}</p>}
                      <small>{formatDate(h.timestamp)}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Link to="/orders" className="btn btn-ghost" style={{ marginTop: 24 }}>← Back to My Orders</Link>
    </div>
  );
}
